/**
 * Utility functions for parsing Fiserv card tokens
 *
 * Token format:
 * - 16 digits total
 * - 1st digit: always 9
 * - 2nd-3rd digits: represent the 1st-2nd digits of the original card number
 * - Last 4 digits: represent the last 4 digits of the original card
 */

export type FiservCardBrand =
  | 'AMEX'
  | 'VISA'
  | 'MASTERCARD'
  | 'DISCOVER'
  | 'DINERS'
  | 'JCB'
  | 'UNKNOWN';

export interface FiservCardInfo {
  brand: FiservCardBrand;
  last4: string;
}

/**
 * Extracts the card brand from a Fiserv token
 * @param token - 16-digit Fiserv token
 * @returns Card brand ('AMEX', 'VISA', 'MASTERCARD', 'DISCOVER', 'DINERS', 'JCB' or 'UNKNOWN')
 */
export const getFiservCardBrandFromToken = (token: string): FiservCardBrand => {
  if (!token || token.length !== 16) {
    return 'UNKNOWN';
  }

  // Get the 2nd and 3rd digits (represent 1st and 2nd digits of original card)
  const firstTwoDigits = token.substring(1, 3);
  const firstDigit = token.charAt(1);

  // VISA: starts with 4
  if (firstDigit === '4') {
    return 'VISA';
  }

  // AMEX: starts with 34 or 37
  if (firstTwoDigits === '34' || firstTwoDigits === '37') {
    return 'AMEX';
  }

  // MASTERCARD: starts with 51-55 or 22-27
  const twoDigitNum = parseInt(firstTwoDigits, 10);
  if ((twoDigitNum >= 51 && twoDigitNum <= 55) || (twoDigitNum >= 22 && twoDigitNum <= 27)) {
    return 'MASTERCARD';
  }

  // DISCOVER: starts with 60, 62-65
  if (firstTwoDigits === '60' || (twoDigitNum >= 62 && twoDigitNum <= 65)) {
    return 'DISCOVER';
  }

  // DINERS: starts with 36 or 38
  if (firstTwoDigits === '36' || firstTwoDigits === '38') {
    return 'DISCOVER';
  }

  // JCB: starts with 35
  if (firstTwoDigits === '35') {
    return 'DISCOVER';
  }

  return 'UNKNOWN';
};

/**
 * Extracts complete card information from a Fiserv token
 * @param token - 16-digit Fiserv token
 * @returns Object containing card brand and last 4 digits
 */
export const getFiservCardInfoFromToken = (token: string): FiservCardInfo => {
  let last4 = '';
  if (!token || token.length !== 16) {
    last4 = '';
  } else {
    last4 = token.slice(-4);
  }

  return {
    brand: getFiservCardBrandFromToken(token),
    last4: last4,
  };
};
